import { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import UserAvatar from '../../userAvatar';
import CustomCheckbox from '../../customCheckbox';
import { ReactComponent as DirectReportArrow } from '../../../../assets/icons/direct-reports-arrow.svg';
import { isArray, isArrayEmpty } from '../../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    borderRadius: 4,
  },
  userItem: {
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2),
    margin: spacing(0, 4),
  },
  hover: {
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  disabledUser: {
    cursor: 'default',
    backgroundColor: primary.bluish9,
    '&:hover': {
      backgroundColor: primary.bluish9,
    },
  },
  userAvatar: {
    width: 'calc(100% - 24px)',
  },
  avatarLabel: {
    color: primary.bluish2,
    fontSize: 16,
    lineHeight: '20px',
    marginLeft: 0,
    paddingRight: spacing(1.5),
    width: '100%',
    userSelect: 'none',
  },
  checkboxContainer: {
    display: 'flex',
    flexShrink: 0,
    width: 24,
    height: 24,
  },
  directReports: {
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    margin: spacing(1, 4, 0, 4),
    padding: spacing(2),
  },
  arrowIcon: {
    marginLeft: spacing(2.75),
    marginBottom: spacing(2.25),
  },
  directReportText: {
    color: primary.bluish2,
    cursor: 'pointer',
    marginLeft: spacing(2),
    flexGrow: 1,
    userSelect: 'none',
  },
}));

const VirtualSuggestion = ({
  translations,
  user,
  directReports = null,
  isUserSelected,
  isDirectReportsSelected = false,
  showDirectReports = false,
  onChange,
  onDirectReportsChange = null,
}) => {
  const classes = useStyles();

  const handleChange = useCallback(() => onChange(user), [user, onChange]);

  const handleDirectReportSelect = useCallback(
    () => (onDirectReportsChange ? onDirectReportsChange(directReports) : null),
    [directReports, onDirectReportsChange]
  );

  return (
    <div className={classes.root}>
      <div
        className={classNames(classes.userItem, classes.hover, {
          [classes.disabledUser]: user.isDisabled,
        })}
      >
        <UserAvatar
          className={classes.userAvatar}
          labelClass={classes.avatarLabel}
          user={user}
          onClickHandler={handleChange}
          clickableCaption={!user.isDisabled}
          caption
          small
        />
        <div className={classes.checkboxContainer}>
          <CustomCheckbox
            isChecked={isUserSelected}
            onChange={handleChange}
            disabled={user.isDisabled}
            isControlled
          />
        </div>
      </div>
      {showDirectReports &&
      isArray(directReports) &&
      !isArrayEmpty(directReports) ? (
        <div className={classNames(classes.directReports, classes.hover)}>
          <DirectReportArrow className={classes.arrowIcon} />
          <Typography
            className={classes.directReportText}
            onClick={handleDirectReportSelect}
          >
            {`${translations.directReports} (${directReports?.length})`}
          </Typography>
          <div className={classes.checkboxContainer}>
            <CustomCheckbox
              isChecked={isDirectReportsSelected}
              onChange={handleDirectReportSelect}
              isControlled
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

VirtualSuggestion.propTypes = {
  translations: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  directReports: PropTypes.arrayOf(PropTypes.object),
  isUserSelected: PropTypes.bool.isRequired,
  isDirectReportsSelected: PropTypes.bool,
  showDirectReports: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDirectReportsChange: PropTypes.func,
};

export default memo(VirtualSuggestion);
